// @tg-seq: 10639
// @tg-golden-rule: BEFORE debugging, validate inside-out (Layer 1-7).
// Read: docs/agent-contracts/AGENT_LEGO_BLOCK_GOLDEN_RULE.md

/**
 * Chat Panel Overlay — Universal Chat Interface
 *
 * Architecture: Drawer overlay pattern (Slack/Teams model)
 * - Opens from anywhere (PlatformHeader icon OR meeting screen button)
 * - Two-panel layout: Conversations list (left) + Message thread (right)
 * - Dual context support:
 *   - Meeting-embedded chats (Organization_UUID context)
 *   - Direct chats from collab core (PFUser_UUID context)
 *
 * Database:
 *   - CHAT.Conversations (via /api/chat-conversations @Manage=5)
 *   - CHAT.Messages (via /api/chat-messages @Manage=5)
 *   - CHAT.AssociationConversations (links to Org + PFUser)
 *
 * Pattern: MUI DataGrid Pro + MUI Drawer + Redux auth dependency
 * Created: 2026-08-20 (refactored from placeholder)
 */

import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MicIcon from '@mui/icons-material/Mic';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Drawer,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../utils/useAuth';
import { getTsxFileName } from '../../utils/componentIdentity';
import axios from 'axios';

interface ChatPanelProps {
    open: boolean;
    onClose: () => void;
    filterContext?: {
        organization_uuid?: string; // Meeting context
        pfuser_uuid?: string; // Direct chat context
    };
}

interface Conversation {
    conversationid: string;
    title: string;
    isgroupchat: boolean;
    createdat: string;
    organization_name?: string;
}

interface Message {
    messageid: string;
    conversationid: string;
    sender_firstname: string;
    sender_lastname: string;
    content: string;
    sentat: string;
    attachments?: Attachment[];
}

interface Attachment {
    attachmentid: string;
    messageid: string;
    filename: string;
    fileurl: string;
    filetype: string;
    filesize_bytes: number;
    duration_seconds?: number;
    transcription_text?: string;
    transcription_status?: string; // 'pending', 'processing', 'completed', 'failed', 'n/a'
    transcription_confidence?: number;
    uploadedat: string;
    isread: boolean;
}

const Self: React.FC<ChatPanelProps> = ({ open, onClose, filterContext }) => {
    const { pfUser, activeOrganizationUUID } = useAuth();

    // State: Conversations list
    const [conversations, setConversations] = useState<GridRowsProp>([]);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

    // State: Messages thread
    const [messages, setMessages] = useState<Message[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [messageInput, setMessageInput] = useState('');

    // State: Attachments
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    // State: Filter dropdown
    const [filterMode, setFilterMode] = useState<'all' | 'meeting' | 'direct'>('all');

    // State: Chat opt-in status (org-scoped 3-tier gate: org-wide enable, association block/opt-out, enrollment)
    const [isOptedIn, setIsOptedIn] = useState<boolean | null>(null); // null = checking
    const [orgChatEnabled, setOrgChatEnabled] = useState<boolean | null>(null);
    const [orgBlocked, setOrgBlocked] = useState(false);
    const [optingIn, setOptingIn] = useState(false);
    const [optInError, setOptInError] = useState<string | null>(null);

    // Check opt-in status on mount (and whenever the active organization changes)
    useEffect(() => {
        if (!open || !pfUser?.user_uuid || !activeOrganizationUUID) return;
        checkOptInStatus();
    }, [open, pfUser?.user_uuid, activeOrganizationUUID]);

    const checkOptInStatus = async () => {
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/api/chat-users`,
                {
                    manage: 9, // STATUS (org gate + association status + enrollment)
                    pfuser_uuid: pfUser?.user_uuid,
                    organization_uuid: activeOrganizationUUID,
                },
            );

            const status =
                response.data.success && response.data.count > 0 ? response.data.data[0] : null;

            setOrgChatEnabled(!!status?.org_chat_enabled);
            setOrgBlocked(status?.association_status_id === 3);
            setIsOptedIn(!!status?.is_enrolled);
        } catch (error) {
            console.error('Error checking opt-in status:', error);
            setOrgChatEnabled(false);
            setOrgBlocked(false);
            setIsOptedIn(false); // Default to not opted in on error
        }
    };

    const handleOptIn = async () => {
        setOptingIn(true);
        setOptInError(null);
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/api/chat-users`,
                {
                    manage: 8, // OPT-IN (idempotent, org-scoped)
                    pfuser_uuid: pfUser?.user_uuid,
                    organization_uuid: activeOrganizationUUID,
                    ttp_id: 0,
                    j: 1,
                },
            );

            if (response.data.success) {
                setIsOptedIn(true);
            }
        } catch (error: any) {
            console.error('Error opting in to chat:', error);
            setOptInError(
                error?.response?.data?.detail || 'Unable to enable chat for this organization.',
            );
        } finally {
            setOptingIn(false);
        }
    };

    // Load conversations on mount or filter change
    useEffect(() => {
        if (!open || !pfUser?.user_uuid) return;
        loadConversations();
    }, [open, pfUser?.user_uuid, filterMode, filterContext]);

    // Load messages when conversation selected
    useEffect(() => {
        if (!selectedConversationId) return;
        loadMessages(selectedConversationId);
    }, [selectedConversationId]);

    const loadConversations = async () => {
        setLoadingConversations(true);
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/api/chat-conversations`,
                {
                    manage: 5, // LIST
                    pfuser_uuid: pfUser?.user_uuid,
                    organization_uuid:
                        filterMode === 'meeting'
                            ? filterContext?.organization_uuid || activeOrganizationUUID
                            : null,
                    ttp_id: 0, // Default
                    j: 1, // Default
                },
            );

            if (response.data.success) {
                setConversations(response.data.data);
            }
        } catch (error) {
            console.error('Error loading conversations:', error);
        } finally {
            setLoadingConversations(false);
        }
    };

    const loadMessages = async (conversationId: string) => {
        setLoadingMessages(true);
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/api/chat-messages`,
                {
                    manage: 5, // LIST
                    conversation_id: conversationId,
                    pfuser_uuid: pfUser?.user_uuid,
                    ttp_id: 0,
                    j: 1,
                },
            );

            if (response.data.success) {
                const messagesData = response.data.data;

                // Load attachments for each message
                const messagesWithAttachments = await Promise.all(
                    messagesData.map(async (msg: Message) => {
                        const attachments = await loadAttachments(msg.messageid);
                        return { ...msg, attachments };
                    }),
                );

                setMessages(messagesWithAttachments);
            }
        } catch (error) {
            console.error('Error loading messages:', error);
        } finally {
            setLoadingMessages(false);
        }
    };

    const handleSendMessage = async () => {
        if (
            (!messageInput.trim() && !selectedFile) ||
            !selectedConversationId ||
            !pfUser?.user_uuid
        )
            return;

        try {
            // Step 1: Create message
            const messageResponse = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/api/chat-messages`,
                {
                    manage: 1, // CREATE
                    conversation_id: selectedConversationId,
                    sender_pfuser_uuid: pfUser.user_uuid,
                    content: messageInput.trim() || '[Attachment]',
                    ttp_id: 0,
                    j: 1,
                },
            );

            if (messageResponse.data.success && selectedFile) {
                const newMessageId = messageResponse.data.data[0]?.messageid;

                if (newMessageId) {
                    // Step 2: Upload file (mock - in production, upload to Azure Blob Storage first)
                    const fileUrl = `https://storage.blob.core.windows.net/chat/${selectedFile.name}`;

                    setUploading(true);

                    // Step 3: Create attachment record
                    await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/chat-attachments`, {
                        manage: 1, // CREATE
                        message_id: newMessageId,
                        file_name: selectedFile.name,
                        file_url: fileUrl,
                        file_type: selectedFile.type,
                        file_size_bytes: selectedFile.size,
                        ttp_id: 0,
                        j: 1,
                    });
                }
            }

            if (messageResponse.data.success) {
                setMessageInput('');
                setSelectedFile(null);
                setUploading(false);
                loadMessages(selectedConversationId); // Refresh thread
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setUploading(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type (audio, MIDI, images, documents)
            const allowedTypes = [
                'audio/wav',
                'audio/mp3',
                'audio/mpeg',
                'audio/webm',
                'audio/ogg',
                'audio/midi',
                'audio/x-midi',
                'image/png',
                'image/jpeg',
                'image/gif',
                'application/pdf',
                'application/msword',
            ];

            if (allowedTypes.includes(file.type) || file.name.endsWith('.mid')) {
                setSelectedFile(file);
            } else {
                alert(
                    'Unsupported file type. Please select audio, MIDI, image, or document files.',
                );
            }
        }
    };

    const loadAttachments = async (messageId: string): Promise<Attachment[]> => {
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/api/chat-attachments`,
                {
                    manage: 5, // LIST
                    message_id: messageId,
                    ttp_id: 0,
                    j: 1,
                },
            );

            if (response.data.success) {
                return response.data.data;
            }
        } catch (error) {
            console.error('Error loading attachments:', error);
        }
        return [];
    };

    const columns: GridColDef[] = [
        {
            field: 'title',
            headerName: 'Conversation',
            flex: 1,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {params.row.title || 'Untitled Conversation'}
                    </Typography>
                    {params.row.organization_name && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {params.row.organization_name}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'isgroupchat',
            headerName: 'Type',
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.row.isgroupchat ? 'Group' : 'Direct'}
                    size="small"
                    color={params.row.isgroupchat ? 'primary' : 'default'}
                />
            ),
        },
    ];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                // Elevated above MUI's default Modal/Dialog zIndex (1300) so this
                // overlay is never buried when triggered from inside another
                // Dialog (Phone Tool, Meeting Master, etc.) — see chatSlice.ts.
                zIndex: (theme) => theme.zIndex.modal + 10,
                '& .MuiDrawer-paper': {
                    width: { xs: '100%', sm: 800 },
                    bgcolor: 'background.default',
                },
            }}
        >
            {/* Header Toolbar */}
            <Toolbar
                sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">Chat</Typography>
                    {/* Opt-in status badge */}
                    {isOptedIn === false && orgChatEnabled === false && (
                        <Chip
                            icon={<ChatBubbleOutlineIcon />}
                            label="Chat Disabled for Org"
                            size="small"
                            sx={{
                                bgcolor: 'grey.500',
                                color: 'common.white',
                                fontWeight: 'bold',
                            }}
                        />
                    )}
                    {isOptedIn === false && orgChatEnabled === true && orgBlocked && (
                        <Chip
                            icon={<ChatBubbleOutlineIcon />}
                            label="Blocked by Org"
                            size="small"
                            sx={{
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                fontWeight: 'bold',
                            }}
                        />
                    )}
                    {isOptedIn === false && orgChatEnabled === true && !orgBlocked && (
                        <Chip
                            icon={<ChatBubbleOutlineIcon />}
                            label="Not Enrolled"
                            size="small"
                            sx={{
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                fontWeight: 'bold',
                            }}
                        />
                    )}
                    {isOptedIn === true && (
                        <Chip
                            label="Enrolled"
                            size="small"
                            sx={{
                                bgcolor: 'success.main',
                                color: 'success.contrastText',
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Select
                        value={filterMode}
                        onChange={(e) => setFilterMode(e.target.value as any)}
                        size="small"
                        sx={{ bgcolor: 'white', minWidth: 120 }}
                    >
                        <MenuItem value="all">All Chats</MenuItem>
                        <MenuItem value="meeting">Meeting Chats</MenuItem>
                        <MenuItem value="direct">Direct Chats</MenuItem>
                    </Select>
                    <IconButton onClick={onClose} sx={{ color: 'primary.contrastText' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Toolbar>

            {/* Opt-in alert (if not enrolled) */}
            {isOptedIn === false && orgChatEnabled === false && (
                <Alert severity="warning" sx={{ m: 2, borderRadius: 2 }}>
                    <AlertTitle>Chat Not Available</AlertTitle>
                    Chat has not been enabled for this organization. Ask your organization admin to
                    turn on "Enable Chat for Opt-in" in the Collaboration Core profile.
                </Alert>
            )}

            {isOptedIn === false && orgChatEnabled === true && orgBlocked && (
                <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
                    <AlertTitle>Chat Access Blocked</AlertTitle>
                    Your organization has blocked chat access for your account.
                </Alert>
            )}

            {isOptedIn === false && orgChatEnabled === true && !orgBlocked && (
                <Alert
                    severity="info"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleOptIn}
                            disabled={optingIn}
                        >
                            {optingIn ? 'Enrolling...' : 'Enable Chat'}
                        </Button>
                    }
                    sx={{ m: 2, borderRadius: 2 }}
                >
                    <AlertTitle>Chat Not Enabled</AlertTitle>
                    Your profile is not enrolled in chat. Click "Enable Chat" to participate in
                    conversations.
                    {optInError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {optInError}
                        </Typography>
                    )}
                </Alert>
            )}

            {/* Two-panel layout */}
            <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
                {/* Left Panel: Conversations List */}
                <Box
                    sx={{
                        width: 300,
                        borderRight: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Search bar */}
                    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search conversations..."
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>

                    {/* Conversations grid */}
                    <DataGrid
                        rows={conversations}
                        columns={columns}
                        getRowId={(row) => row.conversationid}
                        loading={loadingConversations}
                        onRowClick={(params) =>
                            setSelectedConversationId(params.row.conversationid)
                        }
                        hideFooter
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-row': {
                                cursor: 'pointer',
                            },
                            '& .MuiDataGrid-row.Mui-selected': {
                                bgcolor: 'action.selected',
                            },
                        }}
                    />
                </Box>

                {/* Right Panel: Message Thread */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {selectedConversationId ? (
                        <>
                            {/* Messages list */}
                            <List
                                sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    p: 2,
                                    bgcolor: 'grey.50',
                                }}
                            >
                                {loadingMessages ? (
                                    <Typography
                                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                                    >
                                        Loading messages...
                                    </Typography>
                                ) : messages.length === 0 ? (
                                    <Typography
                                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                                    >
                                        No messages yet. Start the conversation!
                                    </Typography>
                                ) : (
                                    messages.map((msg) => (
                                        <ListItem
                                            key={msg.messageid}
                                            alignItems="flex-start"
                                            sx={{ mb: 1 }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar>
                                                    {msg.sender_firstname?.[0]}
                                                    {msg.sender_lastname?.[0]}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 'medium',
                                                            }}
                                                        >
                                                            {msg.sender_firstname}{' '}
                                                            {msg.sender_lastname}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                            }}
                                                        >
                                                            {new Date(
                                                                msg.sentat,
                                                            ).toLocaleTimeString([], {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Paper
                                                            elevation={1}
                                                            sx={{
                                                                p: 1.5,
                                                                mt: 0.5,
                                                                bgcolor: 'background.paper',
                                                            }}
                                                        >
                                                            <Typography variant="body2">
                                                                {msg.content}
                                                            </Typography>
                                                        </Paper>

                                                        {/* Attachments */}
                                                        {msg.attachments &&
                                                            msg.attachments.length > 0 && (
                                                                <Box
                                                                    sx={{
                                                                        mt: 1,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    {msg.attachments.map(
                                                                        (attachment) => {
                                                                            const isAudio =
                                                                                attachment.filetype?.startsWith(
                                                                                    'audio/',
                                                                                );
                                                                            const isMidi =
                                                                                attachment.filetype ===
                                                                                    'audio/midi' ||
                                                                                attachment.filename?.endsWith(
                                                                                    '.mid',
                                                                                );
                                                                            const isVoice =
                                                                                attachment.filetype ===
                                                                                    'audio/voice' ||
                                                                                attachment.filetype?.startsWith(
                                                                                    'audio/',
                                                                                );

                                                                            return (
                                                                                <Paper
                                                                                    key={
                                                                                        attachment.attachmentid
                                                                                    }
                                                                                    elevation={2}
                                                                                    sx={{
                                                                                        p: 1.5,
                                                                                        bgcolor:
                                                                                            'grey.100',
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: 1.5,
                                                                                    }}
                                                                                >
                                                                                    {/* File icon */}
                                                                                    {isMidi ? (
                                                                                        <MusicNoteIcon
                                                                                            sx={{
                                                                                                fontSize: 32,
                                                                                                color: 'primary.main',
                                                                                            }}
                                                                                        />
                                                                                    ) : isVoice ? (
                                                                                        <MicIcon
                                                                                            sx={{
                                                                                                fontSize: 32,
                                                                                                color: 'error.main',
                                                                                            }}
                                                                                        />
                                                                                    ) : isAudio ? (
                                                                                        <AudioFileIcon
                                                                                            sx={{
                                                                                                fontSize: 32,
                                                                                                color: 'secondary.main',
                                                                                            }}
                                                                                        />
                                                                                    ) : (
                                                                                        <AttachFileIcon
                                                                                            sx={{
                                                                                                fontSize: 32,
                                                                                                color: 'text.secondary',
                                                                                            }}
                                                                                        />
                                                                                    )}

                                                                                    {/* File details */}
                                                                                    <Box
                                                                                        sx={{
                                                                                            flex: 1,
                                                                                        }}
                                                                                    >
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                fontWeight:
                                                                                                    'medium',
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                attachment.filename
                                                                                            }
                                                                                        </Typography>
                                                                                        <Box
                                                                                            sx={{
                                                                                                display:
                                                                                                    'flex',
                                                                                                gap: 1,
                                                                                                alignItems:
                                                                                                    'center',
                                                                                                flexWrap:
                                                                                                    'wrap',
                                                                                            }}
                                                                                        >
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: 'text.secondary',
                                                                                                }}
                                                                                            >
                                                                                                {(
                                                                                                    attachment.filesize_bytes /
                                                                                                    1024
                                                                                                ).toFixed(
                                                                                                    1,
                                                                                                )}{' '}
                                                                                                KB
                                                                                            </Typography>
                                                                                            {attachment.duration_seconds && (
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    sx={{
                                                                                                        color: 'text.secondary',
                                                                                                    }}
                                                                                                >
                                                                                                    •{' '}
                                                                                                    {Math.floor(
                                                                                                        attachment.duration_seconds /
                                                                                                            60,
                                                                                                    )}

                                                                                                    :
                                                                                                    {String(
                                                                                                        attachment.duration_seconds %
                                                                                                            60,
                                                                                                    ).padStart(
                                                                                                        2,
                                                                                                        '0',
                                                                                                    )}
                                                                                                </Typography>
                                                                                            )}

                                                                                            {/* Transcription status badge */}
                                                                                            {attachment.transcription_status ===
                                                                                                'pending' && (
                                                                                                <Chip
                                                                                                    label="Transcribing..."
                                                                                                    size="small"
                                                                                                    color="warning"
                                                                                                />
                                                                                            )}
                                                                                            {attachment.transcription_status ===
                                                                                                'completed' && (
                                                                                                <Chip
                                                                                                    label={`Transcribed (${attachment.transcription_confidence?.toFixed(0)}%)`}
                                                                                                    size="small"
                                                                                                    color="success"
                                                                                                />
                                                                                            )}
                                                                                            {attachment.transcription_status ===
                                                                                                'failed' && (
                                                                                                <Chip
                                                                                                    label="Transcription Failed"
                                                                                                    size="small"
                                                                                                    color="error"
                                                                                                />
                                                                                            )}
                                                                                        </Box>

                                                                                        {/* Transcribed text */}
                                                                                        {attachment.transcription_text && (
                                                                                            <Paper
                                                                                                sx={{
                                                                                                    p: 1,
                                                                                                    mt: 1,
                                                                                                    bgcolor:
                                                                                                        'background.paper',
                                                                                                    borderLeft: 3,
                                                                                                    borderColor:
                                                                                                        'primary.main',
                                                                                                }}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    sx={{
                                                                                                        color: 'text.secondary',
                                                                                                        fontWeight:
                                                                                                            'medium',
                                                                                                    }}
                                                                                                >
                                                                                                    📝
                                                                                                    Transcription:
                                                                                                </Typography>
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    sx={{
                                                                                                        mt: 0.5,
                                                                                                        fontStyle:
                                                                                                            'italic',
                                                                                                    }}
                                                                                                >
                                                                                                    {
                                                                                                        attachment.transcription_text
                                                                                                    }
                                                                                                </Typography>
                                                                                            </Paper>
                                                                                        )}
                                                                                    </Box>

                                                                                    {/* Action buttons */}
                                                                                    <Box
                                                                                        sx={{
                                                                                            display:
                                                                                                'flex',
                                                                                            gap: 0.5,
                                                                                        }}
                                                                                    >
                                                                                        {isAudio && (
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    bgcolor:
                                                                                                        'background.paper',
                                                                                                }}
                                                                                            >
                                                                                                <PlayArrowIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        )}
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            sx={{
                                                                                                bgcolor:
                                                                                                    'background.paper',
                                                                                            }}
                                                                                            component="a"
                                                                                            href={
                                                                                                attachment.fileurl
                                                                                            }
                                                                                            download={
                                                                                                attachment.filename
                                                                                            }
                                                                                            target="_blank"
                                                                                        >
                                                                                            <DownloadIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Box>
                                                                                </Paper>
                                                                            );
                                                                        },
                                                                    )}
                                                                </Box>
                                                            )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))
                                )}
                            </List>

                            {/* Message input */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                {/* Selected file preview */}
                                {selectedFile && (
                                    <Alert
                                        severity="info"
                                        sx={{ mb: 1 }}
                                        onClose={() => setSelectedFile(null)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {selectedFile.type.startsWith('audio/') ? (
                                                selectedFile.name.endsWith('.mid') ||
                                                selectedFile.type === 'audio/midi' ? (
                                                    <MusicNoteIcon />
                                                ) : (
                                                    <MicIcon />
                                                )
                                            ) : (
                                                <AttachFileIcon />
                                            )}
                                            <Typography variant="body2">
                                                {selectedFile.name} (
                                                {(selectedFile.size / 1024).toFixed(1)} KB)
                                            </Typography>
                                        </Box>
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                    {/* File upload button */}
                                    <IconButton
                                        component="label"
                                        sx={{
                                            bgcolor: 'action.hover',
                                            '&:hover': { bgcolor: 'action.selected' },
                                        }}
                                        disabled={uploading}
                                    >
                                        <AttachFileIcon />
                                        <input
                                            type="file"
                                            hidden
                                            accept="audio/*,.mid,.midi,image/*,application/pdf"
                                            onChange={handleFileSelect}
                                        />
                                    </IconButton>

                                    <TextField
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        value={messageInput}
                                        onChange={(e) => setMessageInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        placeholder={
                                            selectedFile
                                                ? 'Add a message (optional)'
                                                : 'Type a message...'
                                        }
                                        size="small"
                                        disabled={uploading}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleSendMessage}
                                        disabled={
                                            (!messageInput.trim() && !selectedFile) || uploading
                                        }
                                        sx={{ minWidth: 'auto', px: 2 }}
                                    >
                                        {uploading ? '...' : <SendIcon />}
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: 'text.secondary',
                            }}
                        >
                            <Typography variant="h6">Select a conversation</Typography>
                            <Typography variant="body2">
                                Choose a conversation from the list to view messages
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
